/* eslint-disable max-len */
import React, { useState } from 'react';
import { CCard, CCardHeader, CCardBody, CRow, CCol, CSpinner, CAlert, CBadge, CButton } from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import {
  cilInfo,
  cilFactory,
  cilCart,
  cilCalendar,
  cilMoney,
  cilUser,
  cilDescription,
  cilChevronDoubleDown,
  cilChevronRight
} from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateOnly } from '../../helpers/dateHelper';
import './ProductDetailCard.scss'

const ProductDetailCard = ({ productDetail, isLoading, error }) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState({
    product: false,
    weave: false,
    order: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <div className="loading-spinner">
            <CSpinner size="lg" className="primary-spinner" />
          </div>
          <div className="loading-content">
            <h4 className="loading-title">Processing Request</h4>
            <p className="loading-subtitle">{t('loadingProductDetails')}</p>
            <div className="loading-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state with professional styling
  if (error) {
    return (
      <div className="error-container">
        <CAlert className="error-alert">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <div className="error-text">
              <h5 className="error-title">{t('errorLoadingProductDetails')}</h5>
              <p className="error-message">{error.message || error}</p>
            </div>
          </div>
        </CAlert>
      </div>
    );
  }

  if (!productDetail) {
    return null;
  }

  const { ProductDetail, WeaveDetail, OrderDetail } = productDetail;

  // Use the same date formatting as ProductTable
  const formatDate = (dateValue) => {
    return formatDateOnly(dateValue) || '—';
  };

  // Enhanced currency formatting with proper localization
  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDensityDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numericValue = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(numericValue)) return value;
    const scaledValue = Math.round(numericValue);
    const whole = Math.trunc(scaledValue / 100);
    const decimal = Math.abs(scaledValue % 100).toString().padStart(2, '0');
    return `${whole},${decimal}`;
  };
  // Data field component for consistent rendering
  const DataField = ({ label, value, badge = false, badgeType = 'default', icon = null }) => (
    <div className="data-field">
      <div className="field-header">
        {icon && <CIcon icon={icon} className="field-icon" />}
        <label className="field-label">{label}</label>
      </div>
      <div className="field-value">
        {badge ? (
          <span className={`value-badge badge-${badgeType}`}>{value || '—'}</span>
        ) : (
          <span className="value-text">{value || '—'}</span>
        )}
      </div>
    </div>
  );

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'Onay Bekliyor':
        return 'warning';
      case 'Onaylandı':
        return 'success';
      case 'Reddedildi':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="product-detail-dashboard">

      {/* Product Information Section */}
      <CCard className="intelligence-card primary-card">
        <CCardHeader className="card-header-premium collapsible-header" onClick={() => toggleSection('product')}>
          <div className="header-section">
            <div className="section-icon primary-icon">
              <CIcon icon={cilInfo} />
            </div>
            <div className="section-info">
              <h4 className="section-title">{t('productInformation')}</h4>
            </div>
          </div>
          <CButton
            color="link"
            className="collapse-button"
            onClick={(e) => {
              e.stopPropagation();
              toggleSection('product');
            }}
          >
            <CIcon icon={expandedSections.product ? cilChevronDoubleDown : cilChevronRight} />
          </CButton>
        </CCardHeader>
        <CCardBody className={`card-body-premium ${expandedSections.product ? 'expanded' : 'collapsed'}`}>
          {ProductDetail && ProductDetail.length > 0 && (
            <CRow className="data-grid">
              <CCol lg={4}>
                <div className="field-group">
                  <DataField
                    label={t('certificate')}
                    value={ProductDetail[0].Certificate}
                  />
                </div>
              </CCol>
              <CCol lg={4}>
                <div className="field-group">
                  <DataField
                    label={t('weaveCode')}
                    value={ProductDetail[0].WeaveCode}
                    badge={true}
                    badgeType="weave"
                  />
                </div>
              </CCol>
              <CCol lg={4}>
                <div className="field-group">
                  <DataField
                    label={t('material')}
                    value={ProductDetail[0].MaterialName}
                    badge={true}
                    badgeType="material"
                  />
                </div>
              </CCol>
            </CRow>
          )}
        </CCardBody>
      </CCard>

      {/* Manufacturing Specifications */}
      {WeaveDetail && WeaveDetail.length > 0 && (
        <CCard className="intelligence-card manufacturing-card">
          <CCardHeader className="card-header-premium collapsible-header" onClick={() => toggleSection('weave')}>
            <div className="header-section">
              <div className="section-icon manufacturing-icon">
                <CIcon icon={cilFactory} />
              </div>
              <div className="section-info">
                <h4 className="section-title">{t('weaveDetails')}</h4>
              </div>
            </div>
            <CButton
              color="link"
              className="collapse-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('weave');
              }}
            >
              <CIcon icon={expandedSections.weave ? cilChevronDoubleDown : cilChevronRight} />
            </CButton>
          </CCardHeader>
          <CCardBody className={`card-body-premium ${expandedSections.weave ? 'expanded' : 'collapsed'}`}>
            <CRow className="data-grid">
              <CCol lg={3}>
                <div className="field-group">
                  <DataField
                    label={t('weaveType')}
                    value={WeaveDetail[0].WeaveTypeName}
                  />
                  <DataField
                    label={t('composition')}
                    value={WeaveDetail[0].ThreadName}
                  />
                </div>
              </CCol>
              <CCol lg={2}>
                <div className="field-group">
                  <DataField
                    label={t('warpDensity')}
                    value={formatDensityDisplay(WeaveDetail[0].WarpDensity)}
                    badge={true}
                    badgeType="density"
                  />
                  <DataField
                    label={t('weftDensity')}
                    value={formatDensityDisplay(WeaveDetail[0].WeftDensity)}
                    badge={true}
                    badgeType="density"
                  />
                </div>
              </CCol>
              <CCol lg={2}>
                <div className="field-group">
                  <DataField
                    label={t('reedWidth')}
                    value={WeaveDetail[0].ReedWidth}
                  />
                  <DataField
                    label={t('totalThreads')}
                    value={WeaveDetail[0].TotalThreads}
                    badge={true}
                    badgeType="threads"
                  />
                </div>
              </CCol>
              <CCol lg={2}>
                <div className="field-group">
                  <DataField
                    label={t('reedNo')}
                    value={WeaveDetail[0].ReedNo}
                  />
                  <DataField
                    label={t('dgts')}
                    value={WeaveDetail[0].Dgts}
                  />
                </div>
              </CCol>
              <CCol lg={3}>
                <div className="field-group">
                  <DataField
                    label={t('grayFabricWidth')}
                    value={WeaveDetail[0].FabricWidth}
                  />
                  <DataField
                    label={t('grayFabricWeight')}
                    value={WeaveDetail[0].FabricWeight ? (WeaveDetail[0].FabricWeight / 100).toFixed(2) : null}
                    badge={true}
                    badgeType="weight"
                  />
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      )}

      {/* Commercial Intelligence */}
      {OrderDetail && OrderDetail.length > 0 && (
        <CCard className="intelligence-card commercial-card">
          <CCardHeader className="card-header-premium collapsible-header" onClick={() => toggleSection('order')}>
            <div className="header-section">
              <div className="section-icon commercial-icon">
                <CIcon icon={cilCart} />
              </div>
              <div className="section-info">
                <h4 className="section-title">{t('orderDetails')} ({OrderDetail.length})</h4>
                <p className="section-subtitle">{t('allOrdersForThisProduct')}</p>
              </div>
            </div>
            <CButton
              color="link"
              className="collapse-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('order');
              }}
            >
              <CIcon icon={expandedSections.order ? cilChevronDoubleDown : cilChevronRight} />
            </CButton>
          </CCardHeader>
          <CCardBody className={`card-body-premium ${expandedSections.order ? 'expanded' : 'collapsed'}`}>
            {OrderDetail.map((order, index) => (
              <div key={order.OrderGuid || index} className="order-item">
                <div className="order-header">
                  <h6 className="order-title">{t('order')} #{index + 1}</h6>
                  <CBadge color={getOrderStatusColor(order.Name)} className="order-status-badge">
                    {order.Name}
                  </CBadge>
                </div>
                <CRow className="data-grid order-details-grid">
                  <CCol className="order-col">
                    <div className="field-group">
                      <DataField
                        label={t('customer')}
                        value={order.CustomerName}
                        badge={true}
                        badgeType="customer"
                        icon={cilUser}
                      />
                    </div>
                  </CCol>
                  <CCol className="order-col">
                    <div className="field-group">
                      <DataField
                        label={t('unit')}
                        value={order.UnitName}
                      />
                      <DataField
                        label={t('currency')}
                        value={order.CurrencyName}
                      />
                    </div>
                  </CCol>
                  <CCol className="order-col">
                    <div className="field-group">
                      <DataField
                        label={t('orderDate')}
                        value={formatDate(order.OrderDatetime)}
                        icon={cilCalendar}
                      />
                      <DataField
                        label={t('deadline')}
                        value={formatDate(order.OrderDeadlineDatetime)}
                        icon={cilCalendar}
                      />
                    </div>
                  </CCol>
                  <CCol className="order-col">
                    <div className="field-group">
                      <DataField
                        label={t('quantity')}
                        value={order.ProductQuantity}
                        badge={true}
                        badgeType="quantity"
                      />
                      <DataField
                        label={t('costPrice')}
                        value={formatCurrency(order.ProductCostPrice)}
                        icon={cilMoney}
                      />
                    </div>
                  </CCol>
                  <CCol className="order-col">
                    <div className="field-group">
                      <DataField
                        label={t('salesPrice')}
                        value={formatCurrency(order.ProductSalesPrice)}
                        icon={cilMoney}
                      />
                      {order.OrderDescription && (
                        <DataField
                          label={t('orderDescription')}
                          value={order.OrderDescription}
                          icon={cilDescription}
                        />
                      )}
                    </div>
                  </CCol>
                </CRow>
                {index < OrderDetail.length - 1 && <hr className="order-divider" />}
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};

ProductDetailCard.propTypes = {
  productDetail: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default ProductDetailCard;
